import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { Col, Divider, Flex, Row } from 'antd';
import {
  useFormContext,
  useWatch,
  UseFormReturn,
  UseFormSetValue,
} from 'react-hook-form';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  CODE_MAX_LENGTH,
  DESCRIPTION_MAX_LENGTH,
  NAME_MAX_LENGTH,
} from '../common/constants';
import { FormUserAutoComplete } from '../user/FormUserAutoComplete';
import { DataObjectRecipientsFormField } from './DataObjectRecipientsFormField';
import { DataObjectDescriptionVersionStore } from './DataObjectDescriptionVersionStore';
import { ExecutionFormDataType, RequiredKeyDataType } from './schema';
import translations from './translations/DataObjectForm.translations.json';
import {
  DataObjectFormDataType,
  DataObjectFormType,
  DataObjectOutputType,
  DataObjectType,
  DataObjectView,
  VersionDataType,
} from './types';
import { Padding } from '../common/components';
import {
  filterOption,
  FormCheckbox,
  FormDateRangePicker,
  FormField,
  FormInput,
  FormInputMessages,
  FormSelect,
  useAutoClearFormFieldValueIfNotInOptions,
} from '../common/form';
import { useUserInfo } from '../auth';
import {
  formatCodeName,
  getValidPeriodString,
  isSafeFiniteInteger,
  isValidToday,
} from '../common/utils';
import {
  SystemClassifierOption,
  useSystemClassifierItem,
  useSystemClassifierSelectOptions,
} from '../configuration';
import { DataObjectOutputTypeSelect } from './DataObjectOutputTypeSelect';
import { DataObjectFormAutoComplete } from './DataObjectFormAutoComplete';
import { getGroupId } from './utils';
import {
  DataObjectGroupExtendedDto,
  useDataObjectGroupInfoQuery,
} from '../dataObjectGroup';
import { DataObjectGroupAutoComplete } from './DataObjectGroupAutoComplete';
import { Descriptions } from 'antd';

const { addTranslations, useTranslation } = i18nNsFactory('data-object-table');
addTranslations(translations);

const handleDataObjectTypeChange = (
  setValue: UseFormSetValue<DataObjectFormDataType>,
  outputTypeCode: string,
) => {
  const opts = { shouldDirty: true };
  setValue('formType', outputTypeCode as DataObjectFormType, opts);
  setValue('outputTypeCode', outputTypeCode as DataObjectOutputType, opts);
  if (
    outputTypeCode === DataObjectOutputType.OUTPUT ||
    outputTypeCode === DataObjectOutputType.INTERMEDIATE
  ) {
    setValue('recipientIds', [], opts);
    setValue('confidentialityStatusId', undefined, opts);
    setValue('requiredKeys', [], opts);
    setValue('subjectDimension', true, opts);
  } else {
    setValue('subjectDimension', false, opts);
  }
};

const changeIndicatorStatus = (
  requiredKeys: RequiredKeyDataType[],
  ruleStatusTypes: SystemClassifierOption[],
  ruleValueTypes: SystemClassifierOption[],
  setValue: UseFormSetValue<DataObjectFormDataType>,
  indicatorStatus: boolean,
  indicatorA?: number,
) => {
  const statusId = indicatorStatus ? indicatorA : null;

  const mapping = new Map(
    indicatorStatus
      ? ruleValueTypes.map((vt) => [
          vt.value,
          findClassifierByCode(vt.code, ruleStatusTypes),
        ])
      : ruleStatusTypes.map((st) => [
          st.value,
          findClassifierByCode(st.code, ruleValueTypes),
        ]),
  );

  const opts = { shouldDirty: true };
  for (let i = 0; i < requiredKeys.length; i++) {
    setValue(`requiredKeys.${i}.statusId`, statusId ?? null, opts);
    const ruleTypeId = requiredKeys[i].ruleTypeId;
    if (ruleTypeId) {
      setValue(
        `requiredKeys.${i}.ruleTypeId`,
        mapping.get(ruleTypeId) ?? null,
        opts,
      );
    }
  }
};

const findClassifierByCode = (
  code: string,
  classifiers: SystemClassifierOption[],
) => {
  const val = classifiers.find((sc) => sc.code === code)?.value ?? null;

  if (typeof val === 'number') {
    return val;
  } else if (val && isSafeFiniteInteger(val)) {
    return Number(val);
  }

  return null;
};

export interface DataObjectFormContentProps {
  dataObjectView: DataObjectView;
  mode: 'edit' | 'clone' | 'create';
}

export const DataObjectFormContent: FC<DataObjectFormContentProps> = (
  props,
) => {
  const { dataObjectView, mode } = props;
  const { t } = useTranslation();
  const form = useFormContext<DataObjectFormDataType>();
  const [, setGroupData] =
    DataObjectDescriptionVersionStore.useValue('groupData');
  const { setValue, getValues } = form;
  const [
    allVersions,
    outputDataObjectId,
    outputDataObjectCode,
    outputDataObjectName,
    outputTypeCode,
    statusCode,
    versionNumber,
    validPeriod,
    responsibleId,
    responsibleUsername,
    groupAndSubgroupId,
    domainId,
    accessGroupId,
    frequencyId,
    confidential,
  ] = useWatch({
    name: [
      'allVersions',
      'outputDataObjectId',
      'outputDataObjectCode',
      'outoutDataObjectName',
      'outputTypeCode',
      'statusCode',
      'versionNumber',
      'validPeriod',
      'responsibleId',
      'responsibleUsername',
      'groupAndSubgroupId',
      'domainId',
      'accessGroupId',
      'frequencyId',
      'confidential',
    ],
  });
  const previousGroupAndSubgroupIdRef =
    useRef<DataObjectFormDataType['groupAndSubgroupId']>(groupAndSubgroupId);

  const defaultResponsible = useMemo(() => {
    if (responsibleUsername && responsibleId) {
      return {
        value: responsibleId,
        label: responsibleUsername,
      };
    }
    return undefined;
  }, [responsibleId, responsibleUsername]);

  const statusName = useSystemClassifierItem(
    'DATA_OBJ_GRP_VER_STATUS',
    statusCode,
  )?.name;

  const displayVersionNumber = useMemo(
    () =>
      mode === 'edit'
        ? versionNumber
        : Math.max(
            ...(allVersions as VersionDataType[]).map(
              (item) => item.versionNumber || 0,
            ),
            0,
          ) + 1,
    [allVersions, mode, versionNumber],
  );

  const hasAnyNonDraftVersions = useMemo(
    () =>
      statusCode !== 'DRAFT' ||
      (allVersions as VersionDataType[]).some(
        (item) => item.statusCode !== 'DRAFT',
      ),
    [allVersions, statusCode],
  );

  const systemClassifiers = useSystemClassifierSelectOptions([
    'CONFIDENTIALITY_STATUS',
    'DOMAIN',
    'FREQUENCY',
    'DATA_ORIGIN',
    'DATA_PROVIDER_GROUP',
  ]);
  const {
    IND_STATUS_RULE_TYPE: ruleStatusTypes,
    IND_VALUE_RULE_TYPE: ruleValueTypes,
  } = useSystemClassifierSelectOptions([
    'IND_STATUS_RULE_TYPE',
    'IND_VALUE_RULE_TYPE',
  ]);
  const indicatorA = useSystemClassifierItem('INDICATOR_STATUS', 'A')?.id;

  const isInput = dataObjectView.dataObjectType === DataObjectType.IN;
  const userInfo = useUserInfo();
  const isIntermediate = outputTypeCode === DataObjectOutputType.INTERMEDIATE;
  const isInputWithNonDraftVersions = isInput && hasAnyNonDraftVersions;

  const accessGroupOptions = useMemo(() => {
    const requiredCode = isInput ? 'INPUT_DESC_MANAGE' : 'OUTPUT_DESC_MANAGE';
    const uniqueAccessGroups = new Map(
      userInfo.user.roles
        .filter(
          (role) =>
            isValidToday(role.validPeriod) &&
            role.permissions.some(
              (permission) => permission.code === requiredCode,
            ),
        )
        .map((role) => role.accessGroups)
        .flat(1)
        .map((accessGroup) => [accessGroup.id, accessGroup]),
    ).values();

    return Array.from(uniqueAccessGroups)
      .toSorted((a, b) => a.name.localeCompare(b.name))
      .map((option) => ({
        label: option.name,
        value: option.id,
      }));
  }, [isInput, userInfo.user.roles]);

  useAutoClearFormFieldValueIfNotInOptions({
    name: 'accessGroupId',
    options: accessGroupOptions,
  });

  const handleIndicatorStatusChange = useCallback(
    (event: CheckboxChangeEvent) => {
      changeIndicatorStatus(
        getValues('requiredKeys'),
        ruleStatusTypes,
        ruleValueTypes,
        setValue,
        event.target.checked,
        indicatorA,
      );
    },
    [getValues, indicatorA, ruleStatusTypes, ruleValueTypes, setValue],
  );

  const { data: selectedGroup, isLoading: isGroupInfoLoading } =
    useDataObjectGroupInfoQuery(getGroupId(groupAndSubgroupId) ?? -1, {
      enabled: Boolean(groupAndSubgroupId),
    });

  useEffect(() => {
    if (!groupAndSubgroupId) {
      const opts = { shouldDirty: true };
      const hasGroupBeenRemoved =
        Boolean(previousGroupAndSubgroupIdRef.current) && !groupAndSubgroupId;

      setGroupData(undefined);
      if (hasGroupBeenRemoved) {
        setValue('execution', resetExecution(getValues('execution')), opts);
        setValue('externalCode', '', opts);
        setValue(
          'dataSources',
          detachGroupDataSources(getValues('dataSources')),
          opts,
        );
      }

      previousGroupAndSubgroupIdRef.current = groupAndSubgroupId;
      return;
    }

    if (!selectedGroup) return;
    setGroupData(selectedGroup);

    const hasGroupChanged =
      previousGroupAndSubgroupIdRef.current !== groupAndSubgroupId;
    if (!hasGroupChanged) return;

    updateDependantFields(form, selectedGroup, dataObjectView.dataObjectType);
    previousGroupAndSubgroupIdRef.current = groupAndSubgroupId;
  }, [
    dataObjectView.dataObjectType,
    form,
    getValues,
    groupAndSubgroupId,
    selectedGroup,
    setGroupData,
    setValue,
  ]);

  const hasGroupOrDescriptionNonDraftVersions = useMemo(() => {
    if (dataObjectView.dataObjectType !== DataObjectType.IN) {
      return false;
    }

    if (!groupAndSubgroupId) {
      return hasAnyNonDraftVersions;
    }

    return (
      statusCode !== 'DRAFT' ||
      selectedGroup?.hasAnyNonDraftDataObjectDescriptionVersions
    );
  }, [
    dataObjectView.dataObjectType,
    groupAndSubgroupId,
    statusCode,
    selectedGroup?.hasAnyNonDraftDataObjectDescriptionVersions,
    hasAnyNonDraftVersions,
  ]);

  const [
    { hasActiveSubmittedDataVersions, hasRealData, usedInAlgorithmSource },
  ] = DataObjectDescriptionVersionStore.useValue(
    'dataObjectDescriptionVersion',
  );

  return (
    <>
      <Padding horizontal top bottom>
        <Flex gap="large" vertical>
          {DataObjectType.OUT === dataObjectView.dataObjectType && (
            <FormField
              name="outputTypeCode"
              label={t('fields.outputTypeCode.label')}
              required
            >
              <DataObjectOutputTypeSelect
                outputTypeCode={outputTypeCode}
                onSelect={(value) =>
                  handleDataObjectTypeChange(setValue, value)
                }
              />
            </FormField>
          )}
        </Flex>
      </Padding>
      {isIntermediate && (
        <Padding horizontal>
          <Flex gap="large" vertical>
            <FormField
              label={t('fields.outputDataObject.label')}
              name={'outputDataObjectId'}
              required
            >
              <DataObjectFormAutoComplete
                name="outputDataObjectId"
                placeholder={t('fields.outputDataObject.placeholder')}
                defaultOption={
                  outputDataObjectId
                    ? {
                        value: outputDataObjectId,
                        label: formatCodeName({
                          code: outputDataObjectCode,
                          name: outputDataObjectName,
                        }),
                      }
                    : undefined
                }
                dataObjectType={DataObjectType.OUT}
                outputTypeCodes={[
                  DataObjectOutputType.OUTPUT,
                  DataObjectOutputType.REPORT,
                ]}
                disabled={usedInAlgorithmSource}
              />
            </FormField>
          </Flex>
        </Padding>
      )}

      {isIntermediate && <Divider />}
      <Padding horizontal>
        <Flex gap="large" vertical>
          <FormField
            name="code"
            disabled={hasAnyNonDraftVersions}
            label={t('fields.code.label')}
            placeholder={t('fields.code.placeholder')}
            required
            maxLength={CODE_MAX_LENGTH}
            showCount
          />
          <FormField
            name="name"
            label={t('fields.name.label')}
            placeholder={t('fields.name.placeholder')}
            required
            maxLength={NAME_MAX_LENGTH}
            showCount
          />
          {isIntermediate && (
            <Descriptions
              column={1}
              items={[
                {
                  key: 'domainId',
                  label: t(t('fields.domain.label')),
                  children: systemClassifiers.DOMAIN.find(
                    (option) => option.value === domainId,
                  )?.label,
                },
                {
                  key: 'accessGroupId',
                  label: t(t('fields.accessGroup.label')),
                  children: accessGroupOptions.find(
                    (option) => option.value === accessGroupId,
                  )?.label,
                },
                {
                  key: 'frequencyId',
                  label: t(t('fields.frequency.label')),
                  children: systemClassifiers.FREQUENCY.find(
                    (option) => option.value === frequencyId,
                  )?.label,
                },
                {
                  key: 'responsible',
                  label: t(t('fields.responsible.label')),
                  children:
                    responsibleUsername || t('fields.responsible.placeholder'),
                },
                {
                  key: 'confidential',
                  label: t(t('fields.confidential.label')),
                  children: confidential
                    ? t('fields.confidential.yes')
                    : t('fields.confidential.no'),
                },
              ]}
            />
          )}
          {!isIntermediate && (
            <>
              <FormField
                label={t(
                  'fields.dataObjectGroup.label.' +
                    dataObjectView.dataObjectType,
                )}
                name={'groupAndSubgroupId'}
              >
                <DataObjectGroupAutoComplete
                  name="groupAndSubgroupId"
                  dataObjectType={dataObjectView.dataObjectType}
                  disabled={
                    (mode === 'edit' && !!hasRealData) ||
                    isIntermediate ||
                    isInputWithNonDraftVersions
                  }
                  isLoading={isGroupInfoLoading}
                  defaultOption={selectedGroup}
                  placeholder={t(
                    'fields.dataObjectGroup.placeholder.' +
                      dataObjectView.dataObjectType,
                  )}
                />
              </FormField>
              <FormField
                label={t('fields.domain.label')}
                name={'domainId'}
                required
              >
                <FormSelect
                  disabled={isIntermediate}
                  name={'domainId'}
                  placeholder={t('fields.domain.placeholder')}
                  options={systemClassifiers.DOMAIN}
                  filterOption={filterOption}
                  showSearch
                />
              </FormField>
            </>
          )}
          {DataObjectType.IN === dataObjectView.dataObjectType && (
            <FormField
              label={t('fields.source.label')}
              name={'sourceIds'}
              required
            >
              <FormSelect
                name={'sourceIds'}
                placeholder={t('fields.source.placeholder')}
                options={systemClassifiers.DATA_ORIGIN}
                mode="multiple"
                filterOption={filterOption}
                showSearch
              />
            </FormField>
          )}
          {DataObjectType.IN === dataObjectView.dataObjectType && (
            <FormField
              label={t('fields.dataProviderGroup.label')}
              name={'dataProviderGroupId'}
              required
            >
              <FormSelect
                disabled={isIntermediate}
                name={'dataProviderGroupId'}
                placeholder={t('fields.dataProviderGroup.placeholder')}
                options={systemClassifiers.DATA_PROVIDER_GROUP}
                filterOption={filterOption}
                showSearch
              />
            </FormField>
          )}
          {!isIntermediate && (
            <>
              <FormField
                label={t('fields.accessGroup.label')}
                name={'accessGroupId'}
                required
              >
                <FormSelect
                  disabled={isIntermediate}
                  name={'accessGroupId'}
                  placeholder={t('fields.accessGroup.placeholder')}
                  options={accessGroupOptions}
                  filterOption={filterOption}
                  showSearch
                />
              </FormField>
              <FormField
                label={t('fields.frequency.label')}
                name={'frequencyId'}
                required
              >
                <FormSelect
                  disabled={
                    isIntermediate || hasGroupOrDescriptionNonDraftVersions
                  }
                  name={'frequencyId'}
                  placeholder={t('fields.frequency.placeholder')}
                  options={systemClassifiers.FREQUENCY}
                  filterOption={filterOption}
                  showSearch
                  loading={isGroupInfoLoading}
                />
              </FormField>
            </>
          )}
          {DataObjectType.IN === dataObjectView.dataObjectType && (
            <FormField
              label={t('fields.externalCode.label')}
              placeholder={t('fields.externalCode.placeholder')}
              name={'externalCode'}
              disabled={hasGroupOrDescriptionNonDraftVersions}
            />
          )}
          {!isIntermediate && (
            <FormField
              label={t('fields.responsible.label')}
              name={'responsibleId'}
              required
            >
              <FormUserAutoComplete
                disabled={isIntermediate}
                defaultOption={defaultResponsible}
                styles={{
                  popup: { root: { width: '350px' } },
                }}
                name="responsibleId"
                placeholder={t('fields.responsible.placeholder')}
              />
            </FormField>
          )}
          <Row align="middle">
            {!isIntermediate && (
              <Col span={10}>
                <FormField name={'confidential'}>
                  <FormCheckbox disabled={isIntermediate} name="confidential">
                    {t('fields.confidential.label')}
                  </FormCheckbox>
                </FormField>
              </Col>
            )}
            {DataObjectType.OUT === dataObjectView.dataObjectType && (
              <Col span={10}>
                <FormField name={'subjectDimension'}>
                  <FormCheckbox
                    name="subjectDimension"
                    disabled={hasAnyNonDraftVersions}
                  >
                    {t('fields.subjectDimension.label')}
                  </FormCheckbox>
                </FormField>
              </Col>
            )}
          </Row>
          <FormField name="description" label={t('fields.description.label')}>
            <FormInput
              name="description"
              rows={3}
              maxLength={DESCRIPTION_MAX_LENGTH}
              showCount
            />
          </FormField>
        </Flex>
      </Padding>
      <Divider>
        {(displayVersionNumber || '-') +
          '.' +
          (statusName?.toUpperCase() || '-') +
          ' ' +
          getValidPeriodString(validPeriod)}
      </Divider>
      <Padding horizontal bottom>
        <Flex gap="large" vertical>
          <FormField
            name="validPeriod"
            label={t('fields.validPeriod.label')}
            required
          >
            <FormDateRangePicker name="validPeriod" allowEmpty={[true, true]} />
            <FormInputMessages name="validPeriod.0" />
          </FormField>
          {DataObjectType.IN === dataObjectView.dataObjectType && (
            <FormField name="aggregateValues">
              <FormCheckbox
                name="aggregateValues"
                disabled={hasActiveSubmittedDataVersions}
              >
                {t('fields.aggregateValues.label')}
              </FormCheckbox>
            </FormField>
          )}
          {outputTypeCode === DataObjectOutputType.REPORT && (
            <DataObjectRecipientsFormField />
          )}
          {outputTypeCode === DataObjectOutputType.REPORT && (
            <FormField name={'indicatorStatus'}>
              <FormCheckbox
                name="indicatorStatus"
                onChange={handleIndicatorStatusChange}
              >
                {t('fields.indicatorStatus.label')}
              </FormCheckbox>
            </FormField>
          )}
          {outputTypeCode === DataObjectOutputType.REPORT && (
            <FormField
              label={t('fields.confidentialityStatus.label')}
              name={'confidentialityStatusId'}
              required
            >
              <FormSelect
                name={'confidentialityStatusId'}
                options={systemClassifiers.CONFIDENTIALITY_STATUS}
                filterOption={filterOption}
                showSearch
              />
            </FormField>
          )}
          <FormField
            name="versionDescription"
            label={t('fields.versionDescription.label')}
          >
            <FormInput
              name="versionDescription"
              rows={3}
              maxLength={DESCRIPTION_MAX_LENGTH}
              showCount
            />
          </FormField>
        </Flex>
      </Padding>
    </>
  );
};

const updateDependantFields = (
  form: UseFormReturn<DataObjectFormDataType>,
  dataObjectGroup: DataObjectGroupExtendedDto,
  dataObjectType: DataObjectType,
) => {
  const { dataObjectGroupData, externalCode, execution, dataSources } =
    dataObjectGroup;
  const {
    domain,
    sources,
    dataProviderGroup,
    accessGroup,
    frequency,
    confidential,
    responsible,
  } = dataObjectGroupData ?? {};

  const opts = { shouldDirty: true };
  form.setValue('frequencyId', frequency?.id, opts);
  form.setValue('domainId', domain?.id, opts);
  form.setValue('accessGroupId', accessGroup?.id, opts);
  form.setValue('responsibleId', responsible?.id ?? null!, opts);
  form.setValue('responsibleUsername', responsible?.username ?? '', opts);

  if (dataObjectType === DataObjectType.IN) {
    form.setValue(
      'sourceIds',
      sources?.map((source) => source.id),
      opts,
    );
    form.setValue('dataProviderGroupId', dataProviderGroup?.id, opts);
    form.setValue('confidential', confidential ?? false, opts);
    form.setValue('externalCode', externalCode ?? '', opts);
  }

  if (execution) {
    // If group has execution, replace the existing execution
    form.setValue('execution', execution, opts);
  } else {
    // If group has no execution yet, create it based on existing execution
    form.setValue(
      'execution',
      resetExecution(form.getValues('execution')),
      opts,
    );
  }

  if (dataSources?.length) {
    form.setValue('dataSources', dataSources, opts);
  } else {
    form.setValue(
      'dataSources',
      detachGroupDataSources(form.getValues('dataSources')),
      opts,
    );
  }
};

const detachGroupDataSources = (
  dataSources: DataObjectFormDataType['dataSources'],
) =>
  dataSources?.map((dataSource) => ({
    ...dataSource,
    id: dataSource.groupSource ? null : dataSource.id,
    groupSource: false,
  })) ?? [];

const resetExecution = (
  execution: ExecutionFormDataType,
): ExecutionFormDataType => ({
  ...execution,
  id: null,
  dataObjectGroupId: null,
  executionPeriods: execution.executionPeriods?.map((period) => ({
    ...period,
    id: null,
  })),
  executionCalendar: execution.executionCalendar?.map((entry) => ({
    ...entry,
    id: null,
  })),
  executionPublications: execution.executionPublications?.map((entry) => ({
    ...entry,
    id: null,
  })),
});
